import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { SortLabelButton } from "../../ui/SortLabelButton";
import TablePagination from "@mui/material/TablePagination";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type { AnchorItem, AnchorSortField, SortDirection } from "./useStatisticDetail";

type TabDataState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: AnchorItem[]; totalCount: number }
  | { kind: "error"; message: string };

interface AnchorsTableProps {
  state: TabDataState;
  fetching: boolean;
  page: number;
  pageSize: number;
  sortField: AnchorSortField;
  sortDirection: SortDirection;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (field: AnchorSortField) => void;
  onRefetch: () => void;
}

// Заголовок сортируемой колонки таблицы анкоров.
interface SortableColumn {
  field: AnchorSortField;
  label: string;
  // Отсутствует у текстовых колонок: выравнивание задают только числовым, где
  // разряды должны стоять друг под другом.
  align?: "right";
}

export function AnchorsTable(props: AnchorsTableProps) {
  const {
    state,
    fetching,
    page,
    pageSize,
    sortField,
    sortDirection,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onRefetch,
  } = props;

  const columns: SortableColumn[] = [
    { field: "anchorText", label: "Анкор" },
    { field: "count", label: "Количество", align: "right" },
    { field: "share", label: "Доля", align: "right" },
  ];

  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = page - 1;

  return (
    <Box>
      {fetching && <LinearProgress sx={{ mb: 1 }} />}

      {state.kind === "loading" && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {state.kind === "error" && (
        <Alert
          severity="error"
          action={<Button size="small" onClick={onRefetch}>Повторить</Button>}
        >
          Ошибка загрузки: {state.message}
        </Alert>
      )}

      {state.kind === "empty" && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">Нет анкоров</Typography>
        </Box>
      )}

      {state.kind === "data" && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.align}>
                      <SortLabelButton
                        active={sortField === col.field}
                        direction={sortField === col.field ? sortDirection : "asc"}
                        onClick={() => onSortChange(col.field)}
                      >
                        {col.label}
                      </SortLabelButton>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((item, index) => (
                  <TableRow key={`${item.anchorText}-${index}`} hover>
                    <TableCell>{item.anchorText}</TableCell>
                    <TableCell className="tabular-nums" align="right">{item.count}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                        <Box sx={{ width: 100, height: 8, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
                          <Box
                            sx={{
                              width: `${Math.round(item.share)}%`,
                              height: "100%",
                              bgcolor: "primary.main",
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Typography variant="body2">
                          {item.share.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={state.totalCount}
            page={zeroBasedPage}
            onPageChange={(_event, newPage) => onPageChange(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="На странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          />
        </>
      )}
    </Box>
  );
}
